import React, { useState } from 'react';
import {
  Grid,
  Theme,
  Typography,
  makeStyles,
  useTheme
} from '@material-ui/core';
import {
  DialogComp,
  DialogContentDetails,
  DualActionButton,
  ListItemCell,
  TextInputComponent
} from 'src/components';
import {
  capitalizeFirstLetter,
  isGSTNumber,
  isLandline,
  isPhoneNumber,
  isValidEmail,
  isValidPinCode,
  isWebsiteName
} from 'src/Utils';
import { useTranslation } from 'react-i18next';
import { useEdit } from 'src/hooks/useEdit';
import { API_SERVICES } from 'src/Services';
import { HTTP_STATUSES, USER_TYPE } from 'src/Config/constant';
import toast from 'react-hot-toast';

const useStyles = makeStyles((theme: Theme) => {
  return {
    dialogPaper: {
      width: 847,
      height: 1110,
      padding: theme.spacing(2, 1, 2, 5),
      borderRadius: theme.MetricsSizes.regular
    }
  };
});

type Props = {
  onClose: () => void;
  rowData: any;
  updatedData: any;
  bankData: any;
};

const VendorEditModal = ({
  onClose,
  rowData,
  updatedData,
  bankData
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const classes = useStyles();
  const [isError, setIsError] = useState(false);
  console.log('rowData', rowData);

  const initialValues = {
    company_name: rowData?.company_name || '',
    contact_name: rowData?.contact_name || '',
    phone_no: rowData?.registered_mobile_number || '',
    email_id: rowData?.email_id || '',
    pincode: rowData?.pincode || '',
    website: rowData?.website || '',
    landline_number: rowData?.landline_number || '',
    gst: rowData?.gst || ''
  };

  const addressValue = {
    address_line1: rowData?.address_line1 || '',
    address_line2: rowData?.address_line2 || '',
    address_line3: rowData?.address_line3 || '',
    state: rowData?.state || '',
    city: rowData?.city || '',
    pincode: rowData?.vendor_order_address_pincode || ''
  };

  const bankValue = {
    bank_name: bankData[0]?.name || '',
    account_number: bankData[0]?.account_number || '',
    ifsc_code: bankData[0]?.ifsc_code || ''
  };

  const edit = useEdit(initialValues);
  const editBank = useEdit(bankValue);
  const editAddress = useEdit(addressValue);

  const RequiredFields = ['company_name', 'contact_name'];

  const companyError = isError && !edit.getValue('company_name');
  const contactError = isError && !edit.getValue('contact_name');
  const pincodeError =
    (isError && !edit.allFilled('pincode')) ||
    (isError &&
      edit.allFilled('pincode') &&
      !isValidPinCode(edit.getValue('pincode')));
  const phoneError =
    (isError && !edit.allFilled('phone_no')) ||
    (isError &&
      edit.allFilled('phone_no') &&
      !isPhoneNumber(edit.getValue('phone_no')));
  const websiteError =
    isError &&
    edit.allFilled('website') &&
    !isWebsiteName(edit.getValue('website'));
  const landlineError =
    isError &&
    edit.allFilled('landline_number') &&
    !isLandline(edit.getValue('landline_number'));
  const emailError =
    isError &&
    edit.allFilled('email_id') &&
    !isValidEmail(edit.getValue('email_id'));
  const addressError = isError && !edit.getValue('address');
  const bankNameError = isError && !editBank.getValue('bank_name');
  const accountError = isError && !editBank.getValue('account_number');
  const ifscError =
    (isError && !editBank.getValue('ifsc_code')) ||
    (isError && editBank.getValue('ifsc_code').length < 11);
  const gstError =
    isError && edit.allFilled('gst') && !isGSTNumber(edit.getValue('gst'));
  const RequiredBank = ['bank_name', 'account_number', 'ifsc_code'];

  const renderDialogContent = () => {
    return (
      <>
        <ListItemCell
          avatarImg={rowData?.image_url || 'DUSTMAN'}
          title={capitalizeFirstLetter(rowData?.company_name)}
          titleStyle={{
            fontSize: theme.MetricsSizes.regular_xxx,
            fontWeight: theme.fontWeight.bold
          }}
        />
        <Grid container spacing={1} style={{ marginTop: 20 }}>
          <Grid item xs={12}>
            <TextInputComponent
              inputLabel={t('vendor.companyName')}
              labelColor={theme.Colors.primary}
              value={edit.getValue('company_name')}
              onChange={(e) =>
                edit.update({
                  company_name: capitalizeFirstLetter(e.target.value)
                })
              }
              isError={companyError}
              helperText={companyError && 'Please enter your company name'}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInputComponent
              inputLabel={'Contact Person'}
              labelColor={theme.Colors.primary}
              value={edit.getValue('contact_name')}
              onChange={(e) =>
                edit.update({
                  contact_name: capitalizeFirstLetter(e.target.value)
                })
              }
              isError={contactError}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInputComponent
              inputLabel={'Phone No'}
              labelColor={theme.Colors.primary}
              value={edit.getValue('phone_no')}
              onChange={(e) => {
                if (
                  isNaN(Number(e.target.value)) ||
                  e.target.value?.length > 12
                ) {
                  return;
                }
                edit.update({ phone_no: e.target.value });
              }}
              isError={phoneError}
              inputProps={{
                readOnly: true
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInputComponent
              inputLabel={t('vendor.emailID')}
              labelColor={theme.Colors.primary}
              value={edit.getValue('email_id')}
              onChange={(e) =>
                edit.update({
                  email_id: e.target.value
                })
              }
              isError={emailError}
              helperText={emailError && 'Please enter your valid email id'}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInputComponent
              inputLabel={'GST Number'}
              labelColor={theme.Colors.primary}
              value={edit.getValue('gst')}
              onChange={(e) => edit.update({ gst: e.target.value })}
              isError={gstError}
              helperText={gstError && 'Please enter your valid GST Number'}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInputComponent
              inputLabel={'Address Line1'}
              labelColor={theme.Colors.primary}
              value={editAddress.getValue('address_line1')}
              onChange={(e) =>
                editAddress.update({ address_line1: e.target.value })
              }
              // isError={addressError}
              // helperText={addressError && 'Please enter your address detail'}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInputComponent
              inputLabel={'Address Line2'}
              labelColor={theme.Colors.primary}
              value={editAddress.getValue('address_line2')}
              onChange={(e) =>
                editAddress.update({ address_line2: e.target.value })
              }
              // isError={addressError}
              // helperText={addressError && 'Please enter your address detail'}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInputComponent
              inputLabel={'Address Line3'}
              labelColor={theme.Colors.primary}
              value={editAddress.getValue('address_line3')}
              onChange={(e) =>
                editAddress.update({ address_line3: e.target.value })
              }
              // isError={addressError}
              // helperText={addressError && 'Please enter your address detail'}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInputComponent
              inputLabel={'City'}
              labelColor={theme.Colors.primary}
              value={editAddress.getValue('city')}
              onChange={(e) => editAddress.update({ city: e.target.value })}
              // isError={addressError}
              // helperText={addressError && 'Please enter your address detail'}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInputComponent
              inputLabel={'State'}
              labelColor={theme.Colors.primary}
              value={editAddress.getValue('state')}
              onChange={(e) => editAddress.update({ state: e.target.value })}
              // isError={addressError}
              // helperText={addressError && 'Please enter your address detail'}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInputComponent
              inputLabel={t('enquiry.pincode')}
              labelColor={theme.Colors.primary}
              value={editAddress.getValue('pincode')}
              onChange={(e) =>
                editAddress.update({ pincode: Number(e.target.value) })
              }
              isError={pincodeError}
              helperText={pincodeError && 'Please enter your valid pincode'}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInputComponent
              inputLabel={'Bank Name'}
              labelColor={theme.Colors.primary}
              value={editBank.getValue('bank_name')}
              onChange={(e) =>
                editBank.update({ bank_name: e.target.value.toUpperCase() })
              }
              isError={bankNameError}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInputComponent
              inputLabel={'Account Number'}
              labelColor={theme.Colors.primary}
              value={editBank.getValue('account_number')}
              onChange={(e) => {
                if (isNaN(Number(e.target.value))) {
                  return;
                }
                editBank.update({ account_number: e.target.value });
              }}
              isError={accountError}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInputComponent
              inputLabel={'IFSC Code'}
              labelColor={theme.Colors.primary}
              value={editBank.getValue('ifsc_code')}
              onChange={(e) =>
                editBank.update({ ifsc_code: e.target.value.toUpperCase() })
              }
              isError={ifscError}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInputComponent
              inputLabel={t('vendor.website')}
              labelColor={theme.Colors.primary}
              value={edit.getValue('website')}
              onChange={(e) => edit.update({ website: e.target.value })}
              isError={websiteError}
              helperText={websiteError && 'Please enter your valid website'}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInputComponent
              inputLabel={t('vendor.landlineNumber')}
              labelColor={theme.Colors.primary}
              value={edit.getValue('landline_number')}
              onChange={(e) => edit.update({ landline_number: e.target.value })}
              isError={landlineError}
              helperText={
                landlineError && 'Please enter your valid landline number'
              }
            />
          </Grid>
        </Grid>
      </>
    );
  };

  const handleEditVendor = async () => {
    try {
      if (!edit.allFilled(...RequiredFields)) {
        setIsError(true);
        return toast.error('Please fill all the fields');
      }
      if (!editBank.allFilled(...RequiredBank)) {
        setIsError(true);
        return toast.error('Please fill the bank details');
      }
      let data = { ...edit.edits };
      const response: any =
        await API_SERVICES.customerVendorListService.updateVendor(
          rowData?.id,
          rowData?.vendor_id,
          { data: data, successMessage: 'Vendor Profile Updated Successfully ' }
        );
      if (response.status === HTTP_STATUSES.OK) {
        const bankResponse: any =
          await API_SERVICES.customerVendorListService.getVendorBankDetailsById(
            rowData?.vendor_id
          );
        if (bankResponse.data.data?.length) {
          let data = {
            name: editBank.getValue('bank_name'),
            account_number: editBank.getValue('account_number'),
            ifsc_code: editBank.getValue('ifsc_code'),
            user_id: rowData?.vendor_id,
            user_type: USER_TYPE.vendor,
            connected_id: rowData?.connection_id
          };
          const updateResponse: any =
            await API_SERVICES.customerVendorListService.updateBankDetails(
              bankResponse.data.data[0]?.id,
              {
                data: data
              }
            );
          console.log(updateResponse);
          if (updateResponse.status === HTTP_STATUSES.OK) {
            updatedData();
            onClose();
          }
        } else {
          let data = {
            user_id: rowData?.vendor_id,
            user_type: USER_TYPE.vendor,
            name: editBank.getValue('bank_name'),
            account_number: editBank.getValue('account_number'),
            ifsc_code: editBank.getValue('ifsc_code'),
            connected_id: rowData?.connection_id
          };
          const createResponse: any =
            await API_SERVICES.customerVendorListService.createBankDetails({
              data: data
            });
          console.log(createResponse);
          if (createResponse.status === HTTP_STATUSES.SUCCESS) {
            updatedData();
            onClose();
          }
        }
        let addressData = { ...addressValue, ...editAddress.edits };
        if (rowData?.vendor_order_address_id) {
          const addressResponse: any =
            await API_SERVICES.customerVendorListService.updateVendorAddress(
              rowData?.vendor_order_address_id,
              { data: addressData }
            );
          console.log('addressResponse', addressResponse);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const renderAction = () => {
    return (
      <DualActionButton
        onRightButtonClick={handleEditVendor}
        onLeftButtonClick={onClose}
      />
    );
  };
  return (
    <DialogComp
      open={true}
      onClose={onClose}
      dialogTitle={'Edit Vendor'}
      renderDialogContent={renderDialogContent}
      renderAction={renderAction}
      dialogClasses={{ paper: classes.dialogPaper }}
      dialogTitleStyle={{
        color: theme.Colors.blackMedium
      }}
    />
  );
};

export default VendorEditModal;
